import settings from '@/constants/constants';
import { useFeatureFlag } from '@/hooks/useFeatureFlag';
import { formatAddressString } from '@/utils/address';
import type { UseAddressFormValues } from '@montugroup/circuit-shared';
import { useAddressValidation } from '@montugroup/circuit-shared';
import type { Address } from '@montugroup/circuit-shared/types/address';
import type { AddressDataType, PlaceDetails } from '@montugroup/design-system';
import { Box } from '@montugroup/design-system';
import { LocationInput } from '@montugroup/design-system/LocationInput';
import { styled } from '@mui/material/styles';
import { useState } from 'react';
import type { FieldErrors } from 'react-hook-form';

export type ShippingAddressProps = {
  address: Address;
  onSelectedAddress: (address: Address) => Promise<void>;
  onResetAddress: () => void;
  onManualAddressClick: () => void;
  errors?: FieldErrors<UseAddressFormValues>;
};

const ShippingDetailsContainer = styled(Box)(() => ({
  display: 'flex',
  flexDirection: 'column',
}));

const LocationInputWrapper = styled(Box)(() => ({
  '& .MuiInputBase-input': {
    height: 44,
    paddingTop: 12,
    paddingBottom: 12,
    boxSizing: 'border-box',
  },
}));

const createAddressFromData = (addressData: AddressDataType): Address => {
  const formattedAddress = [addressData.subpremise, addressData.street_number, addressData.address]
    .filter(Boolean)
    .join(' ');

  return {
    addressLine1: formattedAddress,
    addressLine2: '',
    state: addressData.state,
    suburb: addressData.suburb || addressData.city,
    postcode: addressData.postcode,
  };
};

function ShippingAddress({
  address,
  onSelectedAddress,
  onResetAddress,
  onManualAddressClick,
  errors,
}: ShippingAddressProps) {
  const shouldBlockExternalTerritoryPostcodes = useFeatureFlag('enable_blocking_external_territories');
  const [error, setError] = useState<string>('');
  const [inputValue, setInputValue] = useState('');
  const { validateStreetNumber, validatePostcodeNotBlockedFromDelivery } = useAddressValidation();

  const displayValue = inputValue || formatAddressString(address);

  const handleAddressSelected = async (addressData: AddressDataType) => {
    const placeDetails: PlaceDetails = {
      address_components: [
        {
          long_name: addressData.street_number,
          short_name: addressData.street_number,
          types: ['street_number'],
        },
      ],
    };

    const streetNumberValidationResult = validateStreetNumber(placeDetails);
    if (!streetNumberValidationResult.isValid) {
      setError(`${streetNumberValidationResult.errorMessageTitle} ${streetNumberValidationResult.errorMessage}`);
      return;
    }

    if (shouldBlockExternalTerritoryPostcodes) {
      const postcodeBlockedValidationResult = validatePostcodeNotBlockedFromDelivery(addressData.postcode);
      if (!postcodeBlockedValidationResult.isValid) {
        postcodeBlockedValidationResult.errorMessage && setError(postcodeBlockedValidationResult.errorMessage);
        return;
      }
    }

    const updatedAddress = createAddressFromData(addressData);

    setInputValue('');
    await onSelectedAddress(updatedAddress);
  };

  const addressValidationError =
    errors?.addressLine1?.message || errors?.suburb?.message || errors?.state?.message || errors?.postcode?.message;

  //TODO: add Icon in address input
  return (
    <ShippingDetailsContainer>
      <LocationInputWrapper>
        <LocationInput
          variant="outlined"
          value={displayValue}
          shrinkLabel
          name="fullAddress"
          id="location-input"
          googlePlacesApiKey={settings.googlePlacesApiKey}
          error={error || addressValidationError}
          onSelectedAddress={handleAddressSelected}
          onReset={() => {
            setError('');
            setInputValue('');
            onResetAddress();
          }}
          manualAddressFn={onManualAddressClick}
          disableClearable
        />
      </LocationInputWrapper>
    </ShippingDetailsContainer>
  );
}

export default ShippingAddress;
